import InlineText from '../../InlineEdit/InlineText';
import InlineTextArea from '../../InlineEdit/InlineTextArea';
import { useIntl } from 'react-intl';
import messages from './messages';
import { imageScaleUrl } from '../../../helpers/imageScales';
import './MenuSection.scss';

const slugify = (text: string) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const View = (props: any) => {
  const intl = useIntl();
  const { data, isEditMode, block, onChangeBlock, imageSlot } = props;
  const {
    eyebrow,
    title,
    count_label,
    show_allergen_notice,
    header_image,
    items = [],
  } = data;

  const id = title ? slugify(title) : undefined;

  const headerImageUrl =
    Array.isArray(header_image) && header_image.length > 0
      ? imageScaleUrl(header_image[0], 'great')
      : null;

  const headerImageElement = headerImageUrl ? (
    <img src={headerImageUrl} alt={title || ''} loading="lazy" />
  ) : null;
  const headerSlotted = imageSlot?.({
    fieldId: 'header_image',
    value: header_image,
    element: headerImageElement,
  });
  const showHeader = Boolean(headerImageUrl) || Boolean(headerSlotted);

  const update = (field: string, value: string) =>
    onChangeBlock(block, { ...data, [field]: value });

  const updateItem = (index: number, patch: Record<string, any>) => {
    const next = items.map((it: any, i: number) =>
      i === index ? { ...it, ...patch } : it,
    );
    onChangeBlock(block, { ...data, items: next });
  };

  const addItem = () => {
    const next = [
      ...items,
      {
        '@id': newId(),
        title: '',
        description: '',
        price: '',
        price_supplement: '',
        price_accent: false,
        full_width: false,
      },
    ];
    onChangeBlock(block, { ...data, items: next });
  };

  const removeItem = (index: number) => {
    const next = items.filter((_: any, i: number) => i !== index);
    onChangeBlock(block, { ...data, items: next });
  };

  return (
    <article className="menu-section" id={id}>
      <div className="shell">
        {showHeader && (
          <div
            className={`menu-section-banner${headerImageUrl ? '' : ' is-empty'}`}
          >
            {headerSlotted ?? headerImageElement}
          </div>
        )}

        <div className="menu-section-head">
          <div>
            <p className="eyebrow">
              {isEditMode ? (
                <InlineText
                  value={eyebrow ?? ''}
                  onChange={(v) => update('eyebrow', v)}
                  renderTag="span"
                  placeholder={intl.formatMessage(messages.eyebrowPlaceholder)}
                />
              ) : (
                eyebrow
              )}
            </p>
            <h2>
              {isEditMode ? (
                <InlineText
                  value={title ?? ''}
                  onChange={(v) => update('title', v)}
                  renderTag="span"
                  placeholder={intl.formatMessage(
                    messages.sectionTitlePlaceholder,
                  )}
                />
              ) : (
                title
              )}
            </h2>
          </div>
          {(isEditMode || count_label) && (
            <span className="count">
              {isEditMode ? (
                <InlineText
                  value={count_label ?? ''}
                  onChange={(v) => update('count_label', v)}
                  renderTag="span"
                  placeholder={intl.formatMessage(
                    messages.countLabelPlaceholder,
                  )}
                />
              ) : (
                count_label
              )}
            </span>
          )}
        </div>

        {items.length > 0 ? (
          <div className="dish-list">
            {items.map((item: any, i: number) => {
              const itemImageUrl =
                Array.isArray(item.image) && item.image.length > 0
                  ? imageScaleUrl(item.image[0], 'preview')
                  : null;
              const itemImageElement = itemImageUrl ? (
                <img src={itemImageUrl} alt={item.title || ''} loading="lazy" />
              ) : null;
              const itemSlotted = imageSlot?.({
                fieldId: 'items',
                index: i,
                value: item.image,
                element: itemImageElement,
              });
              const showItemImage =
                Boolean(itemImageUrl) || Boolean(itemSlotted);
              // The tags show with or without a description.
              const tags: string[] = Array.isArray(item.allergens)
                ? item.allergens.filter(
                    (t: any) => typeof t === 'string' && t.trim(),
                  )
                : [];
              const itemClassName = `dish-item inline-host${
                showItemImage ? ' has-image' : ''
              }${item.full_width ? ' is-wide' : ''}`;
              return (
                <div className={itemClassName} key={item['@id'] ?? i}>
                  {showItemImage && (
                    <div
                      className={`dish-thumb${itemImageUrl ? '' : ' is-empty'}`}
                    >
                      {itemSlotted ?? itemImageElement}
                    </div>
                  )}
                  <div className="dish-main">
                    <h3>
                      {isEditMode ? (
                        <InlineText
                          value={item.title ?? ''}
                          onChange={(v) => updateItem(i, { title: v })}
                          renderTag="span"
                          placeholder={intl.formatMessage(
                            messages.dishTitlePlaceholder,
                          )}
                        />
                      ) : (
                        item.title
                      )}
                    </h3>
                    {(isEditMode || item.description || tags.length > 0) && (
                      <div className="desc">
                        {isEditMode ? (
                          <InlineTextArea
                            value={item.description ?? ''}
                            onChange={(v) => updateItem(i, { description: v })}
                            renderTag="p"
                            placeholder={intl.formatMessage(
                              messages.dishDescriptionPlaceholder,
                            )}
                          />
                        ) : (
                          item.description && <p>{item.description}</p>
                        )}
                        {tags.length > 0 && (
                          <ul
                            className="dish-tags"
                            aria-label={intl.formatMessage(
                              messages.dishTagsAriaLabel,
                            )}
                          >
                            {tags.map((tag: string) => (
                              <li key={tag} className="dish-tag">
                                {tag}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                  <span
                    className={`price${item.price_accent ? ' accent' : ''}`}
                  >
                    {isEditMode ? (
                      <InlineText
                        value={item.price ?? ''}
                        onChange={(v) => updateItem(i, { price: v })}
                        renderTag="span"
                        placeholder={intl.formatMessage(
                          messages.pricePlaceholder,
                        )}
                        ariaLabel={intl.formatMessage(messages.priceAriaLabel)}
                      />
                    ) : (
                      item.price
                    )}
                    {(isEditMode || item.price_supplement) && (
                      <span className="price-supp">
                        {isEditMode ? (
                          <InlineText
                            value={item.price_supplement ?? ''}
                            onChange={(v) =>
                              updateItem(i, { price_supplement: v })
                            }
                            renderTag="span"
                            placeholder={intl.formatMessage(
                              messages.priceSupplementPlaceholder,
                            )}
                            ariaLabel={intl.formatMessage(
                              messages.priceSupplementAriaLabel,
                            )}
                          />
                        ) : (
                          item.price_supplement
                        )}
                      </span>
                    )}
                  </span>
                  {isEditMode && (
                    <button
                      type="button"
                      className="inline-remove dish-remove"
                      aria-label={intl.formatMessage(messages.removeDish, {
                        title:
                          item.title ||
                          intl.formatMessage(messages.removeDishFallback),
                      })}
                      onClick={() => removeItem(i)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          isEditMode && (
            <p
              style={{
                color: 'var(--brume)',
                fontStyle: 'italic',
                margin: '0 0 var(--space-4)',
              }}
            >
              {intl.formatMessage(messages.emptyState)}
            </p>
          )
        )}

        {isEditMode && (
          <button
            type="button"
            className="inline-add inline-add--full"
            onClick={addItem}
          >
            {intl.formatMessage(messages.addDishButton)}
          </button>
        )}

        {show_allergen_notice && (
          <p className="allergie">
            {intl.formatMessage(messages.allergenNotice)}
          </p>
        )}
      </div>
    </article>
  );
};

export default View;
